import {
  BadgeCheck,
  Bell,
  CircleDollarSign,
  Gift,
  Megaphone,
  MessageSquare,
  Settings,
  TrendingUp,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

/**
 * Player-side metadata for the unified notification types. Mirrors the api's
 * `NOTIFICATION_TYPE_META` (label + tab grouping) but adds the presentation a
 * row needs: whether it carries a market image and which icon to fall back to.
 * Kept here (next to the temporary client shim) so the redesign is self-
 * contained until the Orval client is regenerated.
 */

export type NotificationType =
  | 'ANNOUNCEMENT'
  | 'PROMOTION'
  | 'ACCOUNT'
  | 'SYSTEM'
  | 'WITHDRAW_APPROVED'
  | 'WITHDRAW_FAILED'
  | 'MARKET_RESOLUTION'
  | 'POSITION_VALUE_CHANGE'
  | 'ORDER_FILLED'
  | 'NEW_MARKET'
  | 'LIMIT_ORDER_EXPIRED'
  | 'ORDER_CANCELLED'
  | 'MARKET_CLOSING_SOON'
  | 'MONETIZATION_EXPERIENCE_STARTED'
  | 'MONETIZATION_EXPERIENCE_CLAIMED'
  | 'MONETIZATION_EXPERIENCE_EXPIRING'
  | 'MONETIZATION_EXPERIENCE_EXPIRED'
  | 'SIGNUP_VERIFICATION_REMINDER'
  | 'COMMENT_REPLY';

/** Drawer filter tab. `ALL` applies no type filter. */
export type NotificationTab = 'ALL' | 'MARKETS' | 'ACCOUNT';

/** Icon tone → tinted circle classes for an account/broadcast row's leading icon. */
type IconTone = 'success' | 'danger' | 'accent' | 'muted';

interface TypeMeta {
  label: string;
  /** Markets types carry a `meta.marketImageUrl`; account/broadcast types use `icon`. */
  isMarket: boolean;
  icon: LucideIcon;
  tone: IconTone;
}

/** The market types that filter under the Markets tab. */
export const MARKET_TYPES: NotificationType[] = [
  'MARKET_RESOLUTION',
  'POSITION_VALUE_CHANGE',
  'ORDER_FILLED',
  'NEW_MARKET',
  'LIMIT_ORDER_EXPIRED',
  'ORDER_CANCELLED',
  'MARKET_CLOSING_SOON',
  'COMMENT_REPLY',
];

export const NOTIFICATION_TYPE_META: Record<NotificationType, TypeMeta> = {
  ANNOUNCEMENT: { label: 'Announcement', isMarket: false, icon: Megaphone, tone: 'accent' },
  PROMOTION: { label: 'Promotion', isMarket: false, icon: Gift, tone: 'accent' },
  ACCOUNT: { label: 'Account', isMarket: false, icon: Settings, tone: 'muted' },
  SYSTEM: { label: 'System', isMarket: false, icon: Bell, tone: 'muted' },
  WITHDRAW_APPROVED: { label: 'Withdraw approved', isMarket: false, icon: BadgeCheck, tone: 'success' },
  WITHDRAW_FAILED: { label: 'Withdraw failed', isMarket: false, icon: XCircle, tone: 'danger' },
  MARKET_RESOLUTION: { label: 'Market resolved', isMarket: true, icon: BadgeCheck, tone: 'success' },
  POSITION_VALUE_CHANGE: { label: 'Position value change', isMarket: true, icon: TrendingUp, tone: 'accent' },
  ORDER_FILLED: { label: 'Order filled', isMarket: true, icon: CircleDollarSign, tone: 'success' },
  NEW_MARKET: { label: 'New market', isMarket: true, icon: TrendingUp, tone: 'accent' },
  LIMIT_ORDER_EXPIRED: { label: 'Limit order expired', isMarket: true, icon: XCircle, tone: 'muted' },
  ORDER_CANCELLED: { label: 'Order cancelled', isMarket: true, icon: XCircle, tone: 'muted' },
  MARKET_CLOSING_SOON: { label: 'Market closing soon', isMarket: true, icon: Bell, tone: 'accent' },
  MONETIZATION_EXPERIENCE_STARTED: { label: 'Offer started', isMarket: false, icon: Gift, tone: 'accent' },
  MONETIZATION_EXPERIENCE_CLAIMED: { label: 'Reward claimed', isMarket: false, icon: BadgeCheck, tone: 'success' },
  MONETIZATION_EXPERIENCE_EXPIRING: { label: 'Offer expiring', isMarket: false, icon: Bell, tone: 'accent' },
  MONETIZATION_EXPERIENCE_EXPIRED: { label: 'Offer expired', isMarket: false, icon: XCircle, tone: 'muted' },
  SIGNUP_VERIFICATION_REMINDER: { label: 'Complete your signup', isMarket: false, icon: BadgeCheck, tone: 'accent' },
  // Markets tab (mirrors the api meta) but carries no market image — icon row.
  COMMENT_REPLY: { label: 'Comment reply', isMarket: false, icon: MessageSquare, tone: 'accent' },
};

/** Tinted circle classes for an account/broadcast row icon, keyed by tone. */
export const ICON_TONE_CLASSES: Record<IconTone, string> = {
  success: 'bg-success/15 text-success',
  danger: 'bg-danger/15 text-danger',
  accent: 'bg-accent/15 text-accent',
  muted: 'bg-surface-muted text-muted',
};

/** True for types that carry a market image (subset of the Markets tab). */
export function isMarketType(type: NotificationType): boolean {
  return NOTIFICATION_TYPE_META[type]?.isMarket ?? false;
}
